import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageProvider';
import { translations } from '../i18n/translations';
import MailingRoiCalculator from '../components/MailingRoiCalculator';

type Segment = 'recentSales' | 'propertyData';

const MailingPage: React.FC = () => {
  const { language } = useLanguage();
  const dict = translations[language].mailingPage;
  const [segment, setSegment] = useState<Segment>('recentSales');
  const segmentDict = dict.segments[segment];

  return (
    <div>
      {/* Hero */}
      <section className="bg-sand py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-block px-4 py-2 bg-sky/10 rounded-full text-navy text-sm font-medium mb-6">
              {dict.heroBadge}
            </div>

            {/* Segment toggle */}
            <div className="mb-8">
              <p className="text-sm text-slate mb-3">{dict.segmentToggle.label}</p>
              <div className="inline-flex rounded-full bg-white p-1 shadow-sm border border-slate/10">
                <button
                  type="button"
                  onClick={() => setSegment('recentSales')}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    segment === 'recentSales'
                      ? 'bg-sky text-navy shadow-sm'
                      : 'text-slate hover:text-navy'
                  }`}
                >
                  {dict.segmentToggle.recentSales}
                </button>
                <button
                  type="button"
                  onClick={() => setSegment('propertyData')}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    segment === 'propertyData'
                      ? 'bg-sky text-navy shadow-sm'
                      : 'text-slate hover:text-navy'
                  }`}
                >
                  {dict.segmentToggle.propertyData}
                </button>
              </div>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-navy mb-6 font-space">
              {segmentDict.heroTitle}
            </h1>
            <p className="text-xl md:text-2xl text-slate mb-10 leading-relaxed">
              {segmentDict.heroSub}
            </p>
            <Link
              to="/contact#calendly"
              className="inline-block bg-gradient-to-r from-sky to-sky/80 text-navy px-10 py-5 rounded-full font-semibold hover:shadow-2xl hover:shadow-sky/25 transition-all duration-300"
            >
              {dict.heroCta}
            </Link>
          </div>
        </div>
      </section>

      {/* Fit */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-block px-4 py-2 bg-sky/10 rounded-full text-navy text-sm font-medium mb-4">
              {dict.fit.badge}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-navy font-space">
              {dict.fit.title}
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {dict.fit.items.map((item, index) => (
              <span
                key={index}
                className="px-6 py-3 bg-sand rounded-full text-navy font-medium text-sm md:text-base"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunity */}
      <section className="py-16 md:py-24 bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-sky/10 rounded-full text-navy text-sm font-medium mb-4">
              {segmentDict.opportunity.badge}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4 font-space">
              {segmentDict.opportunity.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center lg:text-left">
              <p className="text-slate text-sm mb-2">{segmentDict.opportunity.regionLabel}</p>
              <p className="text-xl font-bold text-navy mb-6 font-space">{segmentDict.opportunity.regionName}</p>
              <div className="text-6xl md:text-7xl font-bold text-sky font-space mb-2">
                {segmentDict.opportunity.statNumber}
              </div>
              <p className="text-lg text-slate mb-4">{segmentDict.opportunity.statLabel}</p>
              <p className="text-slate leading-relaxed">{segmentDict.opportunity.subtitle}</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 overflow-hidden">
              <h3 className="text-lg font-bold text-navy mb-6 font-space">
                {segmentDict.opportunity.tableTitle}
              </h3>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate/20">
                      {segmentDict.opportunity.tableHeaders.map((header, i) => (
                        <th key={i} className="text-left py-3 px-2 text-navy font-semibold">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {segmentDict.opportunity.rows.map((row, i) => (
                      <tr key={i} className="border-b border-slate/10">
                        <td className="py-3 px-2 text-slate">{row.municipality}</td>
                        <td className="py-3 px-2 text-slate">{row.sold}</td>
                        <td className="py-3 px-2 text-slate">{row.opportunities}</td>
                        <td className="py-3 px-2 font-semibold text-navy">{row.score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="md:hidden space-y-4">
                {segmentDict.opportunity.rows.map((row, i) => (
                  <div key={i} className="p-4 bg-sand/50 rounded-xl">
                    <p className="font-bold text-navy mb-2">{row.municipality}</p>
                    <div className="grid grid-cols-3 gap-2 text-sm text-slate">
                      <div>
                        <span className="block text-xs text-slate/70">{segmentDict.opportunity.tableHeaders[1]}</span>
                        {row.sold}
                      </div>
                      <div>
                        <span className="block text-xs text-slate/70">{segmentDict.opportunity.tableHeaders[2]}</span>
                        {row.opportunities}
                      </div>
                      <div>
                        <span className="block text-xs text-slate/70">{segmentDict.opportunity.tableHeaders[3]}</span>
                        <span className="font-semibold text-navy">{row.score}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="text-sm text-slate/70 text-center max-w-3xl mx-auto">{segmentDict.opportunity.footnote}</p>
        </div>
      </section>

      {/* Value proposition */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-sky/10 rounded-full text-navy text-sm font-medium mb-6">
              {segmentDict.valueProp.badge}
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-navy mb-8 font-space">
              {segmentDict.valueProp.title}
            </h2>
            <p className="text-lg md:text-xl text-slate leading-relaxed">
              {segmentDict.valueProp.body}
            </p>
          </div>
        </div>
      </section>

      {/* Mockups */}
      <section className="py-16 md:py-24 bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-lg font-bold text-navy mb-6 font-space">{segmentDict.letterMockup.title}</h3>
              <div className="border border-slate/20 rounded-xl p-6 bg-sand/30">
                <p className="text-sm text-slate mb-1">{segmentDict.letterMockup.addressee}</p>
                <p className="font-bold text-navy mb-6">{segmentDict.letterMockup.address}</p>
                <h4 className="text-xl font-bold text-navy mb-4 font-space">{segmentDict.letterMockup.heading}</h4>
                <p className="text-slate leading-relaxed whitespace-pre-line mb-6">{segmentDict.letterMockup.body}</p>
                <p className="text-slate whitespace-pre-line text-sm">{segmentDict.letterMockup.signature}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-lg font-bold text-navy mb-6 font-space">{segmentDict.leadMockup.title}</h3>
              <div className="border border-slate/20 rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-bold text-navy">{segmentDict.leadMockup.leadName}</span>
                  <span className="px-3 py-1 bg-sky/20 text-navy text-sm font-medium rounded-full">
                    {segmentDict.leadMockup.leadBadge}
                  </span>
                </div>
                <dl className="space-y-3">
                  {segmentDict.leadMockup.fields.map((field, i) => (
                    <div key={i} className="flex justify-between text-sm border-b border-slate/10 pb-3">
                      <dt className="text-slate">{field.label}</dt>
                      <dd className="font-medium text-navy text-right">{field.value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-6 text-sm text-slate leading-relaxed bg-sky/5 p-4 rounded-lg">
                  {segmentDict.leadMockup.exclusiveNote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-sky/10 rounded-full text-navy text-sm font-medium mb-4">
              {dict.comparison.badge}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4 font-space">
              {dict.comparison.title}
            </h2>
            <p className="text-lg text-slate max-w-3xl mx-auto">{dict.comparison.subtitle}</p>
          </div>

          <div className="hidden md:block overflow-x-auto rounded-2xl shadow-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy text-sand">
                  {dict.comparison.headers.map((header, i) => (
                    <th key={i} className="text-left py-4 px-4 font-semibold">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dict.comparison.rows.map((row, i) => {
                  const isHighlighted = 'highlight' in row && row.highlight;
                  return (
                    <tr
                      key={i}
                      className={isHighlighted ? 'bg-sky/10 border-l-4 border-l-sky' : 'bg-white border-b border-slate/10'}
                    >
                      <td className="py-4 px-4 font-semibold text-navy">{row.channel}</td>
                      <td className="py-4 px-4 text-slate">{row.gets}</td>
                      <td className="py-4 px-4 text-slate">{row.shared}</td>
                      <td className="py-4 px-4 font-semibold text-navy">{row.cost}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-4">
            {dict.comparison.rows.map((row, i) => {
              const isHighlighted = 'highlight' in row && row.highlight;
              return (
                <div
                  key={i}
                  className={`rounded-xl p-5 shadow-sm ${isHighlighted ? 'bg-sky/10 border-l-4 border-l-sky' : 'bg-sand/50'}`}
                >
                  <p className="font-bold text-navy mb-3">{row.channel}</p>
                  <p className="text-sm text-slate mb-2">{row.gets}</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate">{dict.comparison.headers[2]}: {row.shared}</span>
                    <span className="font-semibold text-navy">{row.cost}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-sm text-slate/70 text-center mt-8 max-w-3xl mx-auto">{dict.comparison.footnote}</p>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="py-16 md:py-24 bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-sky/10 rounded-full text-navy text-sm font-medium mb-4">
              {dict.roi.badge}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4 font-space">
              {dict.roi.title}
            </h2>
            <p className="text-lg text-slate">{dict.roi.subtitle}</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <MailingRoiCalculator
              lettersLabel={dict.roi.lettersLabel}
              scanRateLabel={dict.roi.scanRateLabel}
              scanRateHint={dict.roi.scanRateHint}
              jobValueLabel={dict.roi.jobValueLabel}
              jobValuePlaceholder={dict.roi.jobValuePlaceholder}
              metrics={dict.roi.metrics}
              conversionNote={dict.roi.conversionNote}
              assumptions={dict.roi.assumptions}
            />
          </div>
        </div>
      </section>

      {/* Division of labor */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-sky/10 rounded-full text-navy text-sm font-medium mb-6">
              {dict.division.badge}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-6 font-space">
              {dict.division.title}
            </h2>
            <p className="text-lg text-slate leading-relaxed">{dict.division.body}</p>
          </div>
        </div>
      </section>

      {/* Pilot CTA */}
      <section className="py-16 md:py-24 bg-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-sky/20 rounded-full text-sand text-sm font-medium mb-6">
            {dict.pilot.badge}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-sand mb-6 font-space">
            {dict.pilot.title}
          </h2>
          <p className="text-lg text-sand/80 mb-8 max-w-2xl mx-auto leading-relaxed">
            {dict.pilot.body}
          </p>
          <ul className="flex flex-wrap justify-center gap-4 mb-10">
            {dict.pilot.bullets.map((bullet, i) => (
              <li key={i} className="px-4 py-2 bg-sand/10 rounded-full text-sand text-sm">
                {bullet}
              </li>
            ))}
          </ul>
          <Link
            to="/contact#calendly"
            className="inline-block bg-gradient-to-r from-sky to-sky/80 text-navy px-10 py-5 rounded-full font-semibold hover:shadow-2xl hover:shadow-sky/25 transition-all duration-300"
          >
            {dict.pilot.cta}
          </Link>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-slate">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sand/60 text-sm text-center">{dict.disclaimer}</p>
        </div>
      </section>
    </div>
  );
};

export default MailingPage;
